import { useCallback, useState } from 'react'
import {
  createFormControl,
  useController,
  type Control,
  type FieldPath,
  type FieldPathValue,
  type FieldValues
} from 'react-hook-form'

/** Bound to a React Hook Form field: the form owns the value and the error. */
export type FormFieldBindingProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
> = {
  control: Control<TFieldValues>
  name: TName
  disabled?: boolean
  value?: never
  onChange?: never
  onBlur?: never
  error?: never
}

/** Driven by the caller: the caller owns the value and the error. */
export type StandaloneFieldBindingProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
> = {
  control?: never
  name: TName
  value: FieldPathValue<TFieldValues, TName>
  onChange?: (value: FieldPathValue<TFieldValues, TName>) => void
  onBlur?: () => void
  error?: string
  disabled?: boolean
}

export type UseFieldBindingProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
> =
  | FormFieldBindingProps<TFieldValues, TName>
  | StandaloneFieldBindingProps<TFieldValues, TName>

/**
 * Binds one form control to either a React Hook Form `control` or to a
 * caller-supplied `value`/`onChange` pair, and resolves the error message from
 * whichever of the two owns it.
 */
export function useFieldBinding<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>(props: UseFieldBindingProps<TFieldValues, TName>) {
  // Allocated once per instance, and only when there is no control to bind to.
  // `useController` treats the control identity as stable.
  const [detachedControl] = useState(() =>
    props.control ? undefined : createFormControl<TFieldValues>().control
  )

  // The detached control carries no `defaultValues`, so the field level default
  // is what keeps the standalone field from registering as `undefined`.
  const { field, fieldState } = useController({
    control: props.control ?? detachedControl,
    name: props.name,
    defaultValue: props.value,
    disabled: props.disabled
  })

  const boundOnChange = field.onChange
  const boundOnBlur = field.onBlur
  const standaloneOnChange = props.onChange
  const standaloneOnBlur = props.onBlur

  // Emitting mirrors React Hook Form's own `field.onChange` signature. A
  // control that narrows `name` with `FieldPathByValue` already proves at its
  // props boundary that the field holds the shape it emits, and TypeScript
  // cannot reduce `FieldPathValue` while the form type is still generic.
  const onChange = useCallback(
    (...event: Parameters<typeof boundOnChange>) => {
      if (standaloneOnChange) {
        standaloneOnChange(event[0])

        return
      }
      boundOnChange(...event)
    },
    [boundOnChange, standaloneOnChange]
  )

  const onBlur = useCallback(() => {
    boundOnBlur()
    standaloneOnBlur?.()
  }, [boundOnBlur, standaloneOnBlur])

  return {
    name: field.name,
    value: props.control ? field.value : props.value,
    onChange,
    onBlur,
    ref: field.ref,
    disabled: field.disabled,
    error: props.control ? fieldState.error?.message : props.error
  }
}
